import React from "react";
import { Layout, Row, Col, Typography, Divider, Grid } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import logo from "../assets/images/header/logo.svg";
import facebookIcon from "../assets/icons/facebook.svg";
import linkedinIcon from "../assets/icons/linkedin.svg";
import instagramIcon from "../assets/icons/instagram.svg";
import twitterIcon from "../assets/icons/twitter.svg";
import youtubeIcon from "../assets/icons/youtube.svg";
import githubIcon from "../assets/icons/github.svg";
import whatsappIcon from "../assets/icons/whatsapp.svg";
import "./AppFooter.css";
import { getCurrentVillageName } from "../config/villageConfig";
import { useAppContext } from "../context/AppContext";
const { Footer } = Layout;
const { Text } = Typography;

const SocialIcon = ({ iconSrc, altText }) => {
  return (
    <div className="social-icon">
      <img loading="lazy" src={iconSrc} alt={altText} className="social-icon-svg" />
    </div>
  );
};

const AppFooter = () => {
  const { t, i18n } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.xl;
  const ctx = useAppContext(); // safe: returns null when provider missing
  const state = ctx?.state;
  const logoStyle = { height: isMobile ? 30 : 38, display: 'block', color: '#fff' };
  // Navigation mapping for footer items (supports both English and Marathi)
  const footerNavigation = {
    // English keys
    "Citizen Services": "/citizen-services",
    "Government Schemes": "/schemes-projects",
    "Online Applications": "/citizen-services",
    "Document Downloads": "/citizen-services",
    "Tax Payments": "/citizen-services?tab=3",
    "Village Profile": "/our-village",
    "Demographics": null,
    "Infrastructure": "/our-village",
    "Development Projects": "/our-village",
    "News & Updates": "/birthday-wishes",
    "Panchayat Members": "/gram-panchayat",
    "Meeting Minutes": null,
    "Budget Reports": "/transparency-reports",
    "Transparency Portal": "/transparency-reports",
    "Grievance Redressal": "/citizen-services?tab=2",
    "Contact Us": "/gram-panchayat",
    "Emergency Contacts": "/gram-panchayat",
    "Photo Gallery": "/photo-gallery",
    "Events Calendar": "/all-announcements",
    "Feedback": null,
    // Marathi keys
    "नागरिक सेवा": "/citizen-services",
    "सरकारी योजना": "/schemes-projects",
    "ऑनलाइन अर्ज": "/citizen-services",
    "कागदपत्र डाउनलोड": "/citizen-services",
    "कर भरणा": "/citizen-services?tab=3",
    "गाव प्रोफाइल": "/our-village",
    "लोकसंख्या": null,
    "पायाभूत सुविधा": "/our-village",
    "विकास प्रकल्प": "/our-village",
    "बातम्या व अपडेट्स": "/birthday-wishes",
    "पंचायत सदस्य": "/gram-panchayat",
    "सभेच्या कार्यवृत्ता": null,
    "बजेट अहवाल": "/transparency-reports",
    "पारदर्शकता पोर्टल": "/transparency-reports",
    "तक्रार निवारण": "/citizen-services?tab=2",
    "संपर्क करा": "/gram-panchayat",
    "आपत्कालीन संपर्क": "/gram-panchayat",
    "फोटो गॅलरी": "/photo-gallery",
    "कार्यक्रम कॅलेंडर": "/all-announcements",
    "प्रतिक्रिया": null
  };

  return (
    <div className="footer-wrapper">
      {/* Main Footer Section */}
      <Footer className="app-footer section-padding">
        <Row gutter={[32, 32]}>
          {/* Left Section - Logo and Social Icons */}
          <Col xs={24} sm={24} md={8} lg={8} xl={8}>
            <div className="social-icons-container">
              <img loading="lazy" src={logo} alt="Logo" style={{...logoStyle, filter: 'brightness(0) invert(1)'}} className="village-logo" />
              <h4 style={{
                borderRadius: "8px",
                fontFamily: "Teko",
                fontWeight: 500,
                fontSize: 'clamp(16px, 5vw, 24px)', 
                margin: 0, 
                marginBottom: 0,
                color: '#fff',
              }}>
                {i18n.t('')} {getCurrentVillageName(i18n?.language || state?.language || 'mr')}
              </h4>
              <Text className="footer-description">
                {t("footer.description")}
              </Text>
              <SocialIcon iconSrc={facebookIcon} altText="Facebook" />
              <SocialIcon iconSrc={linkedinIcon} altText="LinkedIn" />
              <SocialIcon iconSrc={instagramIcon} altText="Instagram" />
              <SocialIcon iconSrc={twitterIcon} altText="Twitter" />
              <SocialIcon iconSrc={youtubeIcon} altText="YouTube" />
              <SocialIcon iconSrc={githubIcon} altText="GitHub" />
              <SocialIcon iconSrc={whatsappIcon} altText="WhatsApp" />
            </div>
          </Col>
          {/* Right Section - All Categories in a Single Column */}
          <Col xs={24} sm={24} md={16} lg={16} xl={16}>
            <div className="footer-categories-grid">
              {t("footer.categories", { returnObjects: true }).map(
                (category, idx) => (
                  <div key={idx} className="footer-category-single-col mb-4">
                    <Text strong className="footer-category-title">
                      {category.title}
                    </Text>
                    <div className="footer-category-items">
                      {category.items.map((item, index) => {
                        const navLink = footerNavigation[item];
                        return (
                          <div key={index} className="footer-category-item-wrapper">
                            {navLink ? (
                              <Link to={navLink} className="footer-category-item-link">
                                <span className="footer-category-item">{item}</span>
                              </Link>
                            ) : (
                              <span className="footer-category-item">{item}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )}
            </div>
          </Col>
        </Row>
      </Footer>
      
      {/* Bottom Strip */}
      <div className="bottom-strip">
        <div className="bottom-strip-content">
          <div className="copyright-section">
            <Text className="footer-copyright">{t("footer.copyright")}</Text>
          </div>
          <div className="links-section flex items-center">
            <Text className="footer-links">
              {t("footer.termsConditions")}
            </Text>
            <Divider type="vertical" className="footer-links-divider" />
            <Text className="footer-links">
              {t("footer.privacyPolicy")}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppFooter;
